/**
 * The cookie notice, on campaign pages only.
 *
 * Decision of 30 Sep 2026: Consent Mode v2 with a banner, defaults denied.
 * The cost was flagged and accepted — this is the first thing an ad click
 * sees, and it will measurably reduce conversions.
 *
 * So it is built to cost as little as it can: a single strip at the foot of
 * the viewport, not a modal and not an overlay; it never blocks the brief
 * box; and declining is a real button of equal weight rather than a link
 * hidden in the corner. Nothing here uses a browser dialog (repo gotcha #13).
 */

import React, { useState } from 'react';
import { isConfigured } from '../../analytics/gtm';
import { EVENTS, track } from '../../analytics/campaignAnalytics';
import { readConsent, setConsent } from '../../analytics/consent';

export default function CampaignConsent({ skin = 'cmp' }) {
  // `null` = undecided, resolved SYNCHRONOUSLY.
  //
  // This used to start at 'pending' and resolve in an effect, on the theory
  // that it protected a server-rendered first paint. There is no SSR here,
  // and `readConsent` is a synchronous localStorage read that already
  // try/catches — so the effect bought nothing and cost a visible flash:
  // effects run AFTER paint, so the first frame had no bar and the second
  // did, at the foot of the page where it reads as the footer jumping.
  const [choice, setChoice] = useState(readConsent);

  // Nothing to consent to when no container is configured — showing a cookie
  // notice on a page that sets no cookies would be a lie of its own.
  if (!isConfigured()) return null;
  if (choice === 'granted' || choice === 'denied') return null;

  const decide = (next) => {
    setConsent(next);
    setChoice(next);
    // Recorded only when granted. Firing an event to report that someone
    // declined measurement is exactly the thing they declined.
    if (next === 'granted') track(EVENTS.CONSENT, { choice: next });
  };

  // The bar was hard-coded to `cmp-` classes, so in Or's design it wore the
  // other design's buttons — on a screen that is the first thing a cold ad
  // click sees. It takes the skin like every other shared piece.
  const mk = skin === 'mk';
  return (
    <div
      className={mk ? 'cmp-consent mk-consent' : 'cmp-consent'}
      role="region"
      aria-label="Cookie choices"
    >
      <div className="cmp-consent-inner">
        <p className="cmp-consent-text">
          We use analytics cookies to understand how this page is used. Nothing you type here
          is shared with them.
        </p>
        <div className="cmp-consent-actions">
          <button
            type="button"
            className={mk ? 'mk-btn mk-btn--ghost' : 'cmp-btn cmp-btn--secondary'}
            onClick={() => decide('denied')}
          >
            Decline
          </button>
          <button
            type="button"
            className={mk ? 'mk-btn mk-btn--primary' : 'cmp-btn cmp-btn--primary'}
            onClick={() => decide('granted')}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
